import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, switchMap } from 'rxjs';
import { SHARED_ENVIRONMENT } from '@cp/shared/environment/environment.token';
import { Environment } from '@cp/shared/environment/environment.interface';
import { SharedHttpService } from '../../shared/shared-http.service';
import { ProfileHttpPaths, ProfileHttpStaffPaths } from './profile-http.paths';
import {
  AdvancePreferencesHttpModel,
  AppointmentHttpModel,
  EmploymentHttpModel,
  PostcoderCountry,
  PostcoderResult,
  ProfileCautionsAndConvictionsFormRecordHttpModel,
  ProfileHttpModel,
  ResetPasswordHttpModel,
  SkillsHttpModel,
  StaffDetailsHttpModel,
} from './profile-http.models';

@Injectable({
  providedIn: 'root',
})
export class ProfileHttpService extends SharedHttpService {
  private _refreshProfile$ = new BehaviorSubject<void>(undefined);
  private _refreshCautionAndConvictions$ = new BehaviorSubject<void>(undefined);

  public staffDetails$ = this._http.get<StaffDetailsHttpModel>(
    this._environment.apiEndpoint + ProfileHttpPaths.staffDetails,
  );
  public profile$ = this._refreshProfile$.pipe(
    switchMap(() =>
      this._http.get<ProfileHttpModel>(
        this._environment.apiEndpoint + ProfileHttpPaths.profile,
      ),
    ),
  );
  public employment$ = this._http.get<Array<EmploymentHttpModel>>(
    this._environment.apiEndpoint + ProfileHttpPaths.employment,
  );
  public skills$ = this._http.get<Array<SkillsHttpModel>>(
    this._environment.apiEndpoint + ProfileHttpPaths.skills,
  );
  public appointments$ = this._http.get<Array<AppointmentHttpModel>>(
    this._environment.apiEndpoint + ProfileHttpPaths.appointments,
  );
  public getCautionsAndConvictions$ = this._refreshCautionAndConvictions$.pipe(
    switchMap(() =>
      this._http.get<Array<ProfileCautionsAndConvictionsFormRecordHttpModel>>(
        this._environment.apiEndpoint + ProfileHttpPaths.cautionAndConvictions,
      ),
    ),
  );
  public advancePreferences$ = this._http.get<AdvancePreferencesHttpModel>(
    this._environment.apiEndpoint + ProfileHttpPaths.shiftsPreference,
  );

  constructor(
    private _http: HttpClient,
    @Inject(SHARED_ENVIRONMENT)
    private _environment: Environment,
  ) {
    super();
  }

  public refreshProfile(): void {
    this._refreshProfile$.next();
  }

  public refreshCautionAndConvictions(): void {
    this._refreshCautionAndConvictions$.next();
  }

  public updateProfileData(
    url: ProfileHttpStaffPaths,
    data: Record<string, string | null>,
  ): Observable<HttpResponse<void>> {
    return this._http.put<void>(this._environment.apiEndpoint + url, data, {
      observe: 'response',
    });
  }

  public resetPassword(
    data: ResetPasswordHttpModel,
  ): Observable<HttpResponse<void>> {
    return this._http.put<void>(
      this._environment.apiEndpoint + ProfileHttpPaths.resetPassword,
      data,
      {
        observe: 'response',
      },
    );
  }

  public fetchAddressesFromPostcoder(
    country: PostcoderCountry,
    searchTerm: string,
  ): Observable<Array<PostcoderResult>> {
    const encodedSearchTerm = encodeURIComponent(searchTerm.trim());
    return this._http.get<Array<PostcoderResult>>(
      `${this._environment.postcoderApiUrl}/${this._environment.postcoderApiKey}/address/${country}/${encodedSearchTerm}?format=json&lines=2&postcodeonly=true`,
    );
  }

  public submitCautionsAndConvictions(
    data: FormData,
    id: number | null,
  ): Observable<HttpResponse<void>> {
    return this._http.post<void>(
      this._environment.apiEndpoint +
        ProfileHttpPaths.cautionAndConvictions +
        `${id !== null ? `/${id}` : ''}`,
      data,
      {
        params: { submit: true },
        observe: 'response',
      },
    );
  }

  public saveCautionsAndConvictions(
    data: FormData,
    id: number | null,
  ): Observable<HttpResponse<void>> {
    return this._http.post<void>(
      this._environment.apiEndpoint +
        ProfileHttpPaths.cautionAndConvictions +
        `${id !== null ? `/${id}` : ''}`,
      data,
      {
        params: { submit: false },
        observe: 'response',
      },
    );
  }

  public downloadCautionsAndConvictionsCertificate(
    id: number,
  ): Observable<Blob> {
    return this._http.get(
      this._environment.apiEndpoint +
        ProfileHttpPaths.downloadCautionAndConvictionsCertificate.replace(
          '{###}',
          `${id}`,
        ),
      {
        responseType: 'blob',
      },
    );
  }

  public removeCautionsAndConvictions(
    id: number,
  ): Observable<HttpResponse<void>> {
    return this._http.delete<void>(
      this._environment.apiEndpoint +
        ProfileHttpPaths.cautionAndConvictions +
        `/${id}`,
      {
        observe: 'response',
      },
    );
  }
}
